/**
 * Shared helpers for knowledge tools. Extracted from knowledge.ts for reuse
 * across entries, relations, graph, context, and collections compound tools.
 */

// ─── Epistemic Status ─────────────────────────────────────────────────
// Pure derivation of confidence level for claim-carrying entries (BR-85).
// Used by entries/get, commit-entry, and context/gather to surface trust signals.

// BET-281: Epistemic collections have fundamentally different validation logic
// (assumptions use untested→testing→validated/invalidated; insights use hypothesis→evidenced→validated).
// This is intentionally collection-specific (Tier 3 pattern per STD-20).
// When governanceRole is available, replace with governanceRole === 'epistemic' filter.
const EPISTEMIC_COLLECTIONS = new Set(["insights", "assumptions"]);

export interface EpistemicStatus {
  level: "validated" | "evidenced" | "hypothesis" | "untested" | "testing" | "invalidated";
  reason: string;
  action?: string;
}

/**
 * Derives epistemic confidence from entry data + relations.
 * Returns null for non-epistemic entry types.
 */
export function deriveEpistemicStatus(entry: {
  collectionName?: string;
  workflowStatus?: string;
  relations?: Array<{ type?: string; direction?: string }>;
}): EpistemicStatus | null {
  const coll = entry.collectionName?.toLowerCase();
  if (!coll || !EPISTEMIC_COLLECTIONS.has(coll)) return null;

  const relations = entry.relations ?? [];
  const hasValidates = relations.some(
    (r) => r.type === "validates" && r.direction === "incoming",
  );
  const hasInvalidates = relations.some(
    (r) => r.type === "invalidates" && r.direction === "incoming",
  );
  const evidenceCount = relations.filter(
    (r) => (r.type === "validates" || r.type === "invalidates") && r.direction === "incoming",
  ).length;

  if (coll === "assumptions") {
    const ws = entry.workflowStatus;
    if (ws === "invalidated" || hasInvalidates) {
      return { level: "invalidated", reason: "Disproved by linked counter-evidence" };
    }
    if (ws === "validated" || (hasValidates && ws !== "untested")) {
      return { level: "validated", reason: `${evidenceCount} evidence link${evidenceCount !== 1 ? "s" : ""}` };
    }
    if (ws === "testing") {
      return { level: "testing", reason: "Experiment in progress" };
    }
    return { level: "untested", reason: "No evidence linked", action: "Link evidence via `relations action=create type=\"validates\"`" };
  }

  // Insights: derive from workflowStatus first, then from relations (BR-85).
  // Deliberate AND: workflowStatus alone isn't enough — linked evidence is required.
  // Setting ws="validated" without evidence still returns "hypothesis" per BR-85.
  const ws = entry.workflowStatus;
  if (ws === "validated" && hasValidates) {
    return { level: "validated", reason: `${evidenceCount} evidence link${evidenceCount !== 1 ? "s" : ""}` };
  }
  if (ws === "evidenced" || hasValidates) {
    return { level: "evidenced", reason: `${evidenceCount} evidence link${evidenceCount !== 1 ? "s" : ""}` };
  }
  // BR-85: insight without linked evidence defaults to hypothesis
  return {
    level: "hypothesis",
    reason: "No linked evidence",
    action: "Link proof via `relations action=create type=\"validates\"`",
  };
}

/** Format epistemic status as a single markdown line for MCP text responses. */
export function formatEpistemicLine(es: EpistemicStatus): string {
  const icon = es.level === "validated" ? "✓"
    : es.level === "evidenced" ? "◎"
    : es.level === "hypothesis" ? "△"
    : es.level === "untested" ? "?"
    : es.level === "testing" ? "◎"
    : "✕";
  const suffix = es.action ? ` ${es.action}` : "";
  return `**Confidence:** ${icon} ${es.level} — ${es.reason}.${suffix}`;
}

/** Adapts a raw entry record into the shape deriveEpistemicStatus expects. */
export function toEpistemicInput(entry: Record<string, unknown>) {
  return {
    collectionName: typeof entry.collectionName === "string" ? entry.collectionName : undefined,
    workflowStatus: typeof entry.workflowStatus === "string" ? entry.workflowStatus : undefined,
    relations: Array.isArray(entry.relations)
      ? (entry.relations as Array<{ direction?: string; type?: string }>).map((r) => ({ type: r.type, direction: r.direction }))
      : undefined,
  };
}

/** Compact suffix for list/context views. Only emits for low-confidence entries. */
export function epistemicSuffix(es: EpistemicStatus | null): string {
  if (!es) return "";
  if (es.level === "validated") return "";
  if (es.level === "evidenced") return "";
  if (es.level === "hypothesis") return " `△ hypothesis`";
  if (es.level === "untested") return " `? untested`";
  if (es.level === "testing") return " `◎ testing`";
  if (es.level === "invalidated") return " `✕ invalidated`";
  return "";
}

// ─── General helpers ──────────────────────────────────────────────────

export function extractPreview(data: unknown, maxLen: number): string {
  if (!data || typeof data !== "object") return "";
  const d = data as Record<string, unknown>;
  const raw = d.description ?? d.canonical ?? d.detail ?? d.rule ?? "";
  if (typeof raw !== "string" || !raw) return "";
  return raw.length > maxLen ? raw.substring(0, maxLen) + "..." : raw;
}

/**
 * Canonical tool-name translation map.
 * Maps deprecated atomic tool names to their consolidated compound equivalents.
 * Used by context/orient to annotate stale references in historical content
 * (plans, docs, prototypes) without bulk-editing those files.
 *
 * WP-484 S3 (clean break, TEN-2157): retargeted + extended. Two changes:
 * (1) pre-WP-484 entries whose RHS pointed at a name that ALSO retired in S3
 * (`graph`, `health`, `session-wrapup`) are retargeted to the new noun so the
 * coach never routes an agent toward a second dead name; (2) the 25 tool
 * names that retired in S3 (37 total − 12 reused: the 9 core nouns plus
 * `chain` and `chain-review` — `chain-review` was REUSED as a compound name
 * in S2, a ruled deviation from the original 11-reused/26-retiring count) are
 * added as new entries, so a stale reference to any pre-S3 name gets coached
 * to current syntax.
 */
export const TOOL_NAME_MIGRATIONS: ReadonlyMap<string, string> = new Map([
  // ─── Pre-WP-484 atomic names (retargeted where the old RHS also retired) ──
  ["list-entries", 'entries action="list"'],
  ["get-entry", 'entries action="get"'],
  ["batch-get", 'entries action="batch"'],
  ["search", 'entries action="search"'],
  ["relate-entries", 'relations action="create"'],
  ["batch-relate", 'relations action="batch-create"'],
  ["find-related", 'relations action="find"'],
  ["suggest-links", 'relations action="suggest"'],
  ["gather-context", 'context action="gather"'],
  ["get-build-context", 'context action="build"'],
  ["list-collections", 'collections action="list"'],
  ["create-collection", 'collections action="create"'],
  ["update-collection", 'collections action="update"'],
  ["agent-start", 'session action="start"'],
  ["agent-close", 'session action="close"'],
  ["agent-status", 'session action="status"'],
  ["workspace-status", 'workspace action="status"'],
  ["mcp-audit", 'workspace action="audit"'],
  ["quality-check", 'quality action="check"'],
  ["re-evaluate", 'quality action="re-evaluate"'],
  ["list-workflows", 'workflows action="list"'],
  ["workflow-checkpoint", 'workflows action="checkpoint"'],
  ["wrapup", 'session action="wrapup-review"'],
  ["finish", 'session action="wrapup-review"'],
  // ─── WP-484 S3 clean break: the 25 retiring standalone tool names ────────
  // Multi-action retired tools map to the FULL action menu (pipe / wildcard
  // form), never a single default action: the coach only matches the retired
  // NAME, so historical text like `facilitate action=commit-constellation`
  // would otherwise be annotated with the wrong action (e.g. → resume) and
  // send the agent down the wrong path (PR #362 review finding).
  ["update-entry", 'entries action="update"'],
  ["get-history", 'entries action="history"'],
  ["commit-entry", 'entries action="commit"'],
  ["move-entry", 'entries action="move"'],
  ["session-wrapup", 'session action="wrapup-review|wrapup-commit" (review → wrapup-review, commit-all → wrapup-commit)'],
  ["facilitate", 'session action="resume|commit-constellation" (same action names)'],
  ["graph", 'relations action="find|suggest" (same action names)'],
  ["documents", 'context action="last-verified-brief"'],
  ["labels", 'collections action="label-*" (list/create/update/delete/apply/remove → label-<same>)'],
  ["health", 'workspace action="check|whoami|status|audit|self-test" (same action names)'],
  ["verify", 'quality action="verify-chain"'],
  ["verify-entry", 'entries action="verify"'],
  ["batch-capture", 'capture action="batch" (entries[] → items[])'],
  ["skills", 'workflows action="load-skill"'],
  ["start_pb", 'orient action="start"'],
  ["record_activation", 'orient action="record-activation"'],
  ["get-usage-summary", 'workspace action="usage"'],
  ["audit", 'quality action="audit"'],
  ["governance-proposals", 'workspace action="proposals-list|proposals-respond|proposals-count" (list/respond/count → proposals-<same>)'],
  ["chain-version", 'chain action="version.*" (commit/list/diff/revert/history → version.<same>)'],
  ["chain-branch", 'chain-review action="branch.*" (create/list/merge/conflicts → branch.<same>)'],
  ["create-audience-map-set", 'map action="create-audience-set"'],
  ["map-slot", 'map action="slot.*" (add/remove/replace/list → slot.<same>)'],
  ["map-version", 'map action="version.*" (commit/list/history → version.<same>)'],
  ["map-suggest", 'map action="suggest"'],
]);

/**
 * Translates any deprecated tool names found in text to their current equivalents.
 * Returns null if no translations were needed, or a footnote string listing them.
 */
export function translateStaleToolNames(text: string): string | null {
  const found: string[] = [];
  for (const [old, current] of TOOL_NAME_MIGRATIONS) {
    const pattern = new RegExp(`\\b${old.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
    if (pattern.test(text)) {
      found.push(`\`${old}\` → \`${current}\``);
    }
  }
  if (found.length === 0) return null;
  return `\n\n---\n_Tool name translations (these references use deprecated names):_\n${found.map(f => `- ${f}`).join("\n")}`;
}
